import {
  ActionMessage,
  ActionMessageFieldOption,
} from '@app/graphql-types/Source'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { cardWithLeftHighlightStyles } from '../../../../javascript/styles'
import { useI18n } from '../../../configuration/contexts/I18nContext'
import styled from '../../constants/theme'
import ActionMessageDialogContext from '../../contexts/ActionMessageDialogContext'
import { cache } from '../../graphql/cache'
import { Badge } from '../elements/Badge'
import PillButton from '../elements/PillButton'
import Form from '../forms/Form'
import { CommonFormElementProps } from '../forms/FormElementParts'

const PharmacySearchResultList: React.FC<{
  controlName: string
  data: any[]
}> = ({ controlName, data }) => {
  const i18n = useI18n()
  const { setValue } = useFormContext()
  const [selectedValue, setSelectedValue] = React.useState<string>()
  const handlePharmacySelect = (e: any) => {
    setSelectedValue((_prev) => {
      setValue(controlName, e.target.value, { shouldDirty: true })
      return e.target.value
    })
  }

  return (
    <SearchResults>
      <h2>{i18n.t('assessments.pharmacySelect.searchResults')}</h2>
      <PharmacyList
        name={controlName}
        onChange={handlePharmacySelect}
        options={data.map((d) => ({
          ...d,
          label: d.name,
          value: d.id,
        }))}
        value={selectedValue}
      />
    </SearchResults>
  )
}
export const PharmacySearchResults: React.FC<{
  data: any[]
  onBackClick: () => void
  onPharmacySelect: (field: any) => void
}> = ({ data, onBackClick, onPharmacySelect }) => {
  const { screenIndex, setOtherScreen, trackPage } = React.useContext(
    ActionMessageDialogContext,
  )
  const i18n = useI18n()
  const controlName = 'pharmacyId'
  const { streamId } = useParams<{ streamId: string }>()

  const handleSubmit = (formData) => {
    const theId = formData[controlName]

    const foundData = data.find((d) => d.id.toString() === theId)
    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        [`actionMessage({"name":"rx","streamId":"${streamId}"})`]: (
          existing: ActionMessage,
        ) => {
          const newScreens = existing.form?.screens?.map((screen) => {
            const newFields = screen.fields?.map((field) => {
              // Not the field type we want or no options
              if (field.type !== 'pharmacy_select' || !field.options) {
                return field
              }

              const foundIndex = field.options.findIndex(
                (opt) => opt.value === theId,
              )
              // Option already exists, move it to the top
              if (foundIndex > -1) {
                if (foundIndex === 0) {
                  return field
                }

                const newOptions = [...field.options]
                newOptions.unshift(newOptions.splice(foundIndex, 1)[0])
                return { ...field, options: newOptions }
              }

              const { id, name, ...rest } = foundData
              const newOption = {
                ...rest,
                __typename: 'ActionMessageFieldOption',
                label: name,
                value: id,
              }
              const newField = {
                ...field,
                options: [newOption, ...field.options],
                value: theId,
              }
              return newField
            })

            return { ...screen, fields: newFields }
          })

          return {
            ...existing,
            form: {
              ...existing.form,
              screens: newScreens,
            },
          }
        },
      },
    })

    onPharmacySelect({ pharmacy: theId })
    trackPage('next', screenIndex, undefined, 'pharmacy_added')
    setOtherScreen(null)
  }

  return (
    <Form
      additionalButtons={[
        <PillButton onClick={onBackClick}>
          {i18n.t('pagination.back')}
        </PillButton>,
      ]}
      defaultValues={{ [controlName]: '' }}
      onSubmit={handleSubmit}
      submitButtonText={i18n.t('pagination.next')}
      validationSchema={yup.object()}
    >
      <PharmacySearchResultList controlName={controlName} data={data} />
    </Form>
  )
}

interface SharedProps {
  name: CommonFormElementProps['name']
  onChange: CommonFormElementProps['onChange']
  options: ActionMessageFieldOption[]
  value: CommonFormElementProps['value']
}
const PharmacyList: React.FC<SharedProps> = ({
  name,
  onChange,
  options,
  value,
}) => {
  return (
    <ListWrap className="PharmacyList">
      {options.map((option, i) => {
        const cityArr: string[] = []
        option.city && cityArr.push(option.city)
        option.state && cityArr.push(option.state)
        option.zipcode && cityArr.push(option.zipcode)
        const cityLine =
          cityArr.length > 1 ? cityArr.join(', ') : cityArr.join('')
        const specialties = (option.specialties || []).map((s, i) => (
          <Badge key={i} className="Specialty">
            {s}
          </Badge>
        ))
        const listItems = [
          option.label,
          option.addressLine1,
          option.addressLine2,
          cityLine || null,
          specialties.length ? specialties : null,
        ]
        const { value: optionValue } = option
        const elementId = `pharm-select-item-${optionValue}`
        return (
          <ItemWrap key={i}>
            <label htmlFor={elementId}></label>
            <input
              checked={optionValue === value}
              id={elementId}
              name={name}
              onChange={onChange}
              type="radio"
              value={optionValue}
            />
            <UnorderedList>
              {listItems.map((item, j) => {
                return item ? <li key={j}>{item}</li> : null
              })}
            </UnorderedList>
          </ItemWrap>
        )
      })}
    </ListWrap>
  )
}

const PharmacySelect: React.FC<Partial<CommonFormElementProps>> = ({
  name,
  onChange,
  options,
  value,
}) => {
  const i18n = useI18n()
  const { screenIndex, setOtherScreen, trackPage } = React.useContext(
    ActionMessageDialogContext,
  )
  const addPharmClick = React.useCallback(() => {
    trackPage('add_pharmacy', screenIndex, 'subscreen_1')
    setOtherScreen({ name: 'my-sweet-screen' })
  }, [screenIndex, setOtherScreen, trackPage])
  const theOptions = options as unknown as ActionMessageFieldOption[]
  const handlePharmSelectChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      name && onChange?.({ [name]: e.target.value })
    },
    [name, onChange],
  )

  return (
    <Wrapper className="PharmacySelect">
      {theOptions?.length > 0 && (
        <PharmacyList
          name={name}
          onChange={handlePharmSelectChange}
          options={theOptions}
          value={value}
        />
      )}

      <AddPharmWrap>
        <PillButton type="button" onClick={addPharmClick}>
          {i18n.t('assessments.pharmacySelect.addPharmacy')}
        </PillButton>
      </AddPharmWrap>
    </Wrapper>
  )
}

const AddPharmWrap = styled.div({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
})

const ItemWrap = styled.div(({ theme }) => ({
  position: 'relative',
  '&:hover ul': {
    backgroundColor: theme.greyLight,
  },
  '& [type="radio"]': {
    display: 'none',
  },
  '& label': {
    cursor: 'pointer',
    height: '100%',
    position: 'absolute',
    width: '100%',
    zIndex: 1,
  },
  '& :checked + ul': {
    backgroundColor: theme.greyMedium,
  },
}))

const ListWrap = styled.div(({ theme }) => ({
  maxHeight: 650,
  overflow: 'auto',
  '& .Specialty': {
    alignItems: 'center',
    backgroundColor: theme.primary,
    display: 'inline-flex',
    fontSize: 12,
    padding: 10,
    width: 'auto',
    '& + .Specialty': {
      marginLeft: 3,
    },
  },
}))

const SearchResults = styled.div({
  '& h2': {
    lineHeight: 1,
    marginBottom: 15,
  },
})

const UnorderedList = styled.ul(({ theme }) => ({
  ...cardWithLeftHighlightStyles({ theme }),
  display: 'flex',
  flexDirection: 'column',
  listStyle: 'none',
  margin: 0,
  marginBottom: 15,
  padding: 24,
  paddingLeft: 44,
  '& > li': {
    alignItems: 'center',
    display: 'inline-flex',
    height: 24,
  },
  '& li:first-of-type': {
    fontSize: 18,
    fontWeight: 700,
  },
}))

const Wrapper = styled.div({
  marginBottom: 10,
  marginTop: 25,
  '& .PharmacySelectZeroState': {
    marginBottom: 15,
    textAlign: 'center',
  },
})

export default PharmacySelect
